import { useState, useEffect } from 'react';
import { projectFirestore } from '../firebase/config';
import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET } from '../cloudinary/config';

const DEFAULT_CONFIG = {
    activeSource: 'cloudinary',
    cloudinaryAccounts: [{
        id: 'default',
        label: 'الحساب الأساسي',
        cloudName: CLOUDINARY_CLOUD_NAME,
        preset: CLOUDINARY_UPLOAD_PRESET,
        enabled: true,
    }],
    firebaseAccounts: [],
    activeCloudinary: 'default',
    activeFirebase: '',
};

const useImageSource = () => {
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsub = projectFirestore.collection('settings').doc('imageSource')
            .onSnapshot((doc) => {
                if (doc.exists) {
                    const data = doc.data();
                    setConfig({
                        activeSource: data.activeSource || 'cloudinary',
                        cloudinaryAccounts: (data.cloudinaryAccounts || []).map(a => ({ ...a, enabled: a.enabled !== false })),
                        firebaseAccounts: (data.firebaseAccounts || []).map(a => ({ ...a, enabled: a.enabled !== false })),
                        activeCloudinary: data.activeCloudinary || '',
                        activeFirebase: data.activeFirebase || '',
                    });
                }
                setLoading(false);
            });
        return () => unsub();
    }, []);

    // الحصول على رابط العرض — يحترم المصدر المحدد بدقة
    const getDisplayUrl = (doc) => {
        if (!doc) return '';
        const source = config.activeSource; // 'cloudinary' | 'firebase' | 'auto'

        if (doc.sources) {
            if (source === 'auto') {
                // تلقائي: جرب Cloudinary أولاً ثم Firebase ثم أي مصدر متاح
                const cId = config.activeCloudinary;
                if (cId && doc.sources[cId]) return doc.sources[cId];
                const fId = config.activeFirebase;
                if (fId && doc.sources[fId]) return doc.sources[fId];
                const anyUrl = Object.values(doc.sources)[0];
                if (anyUrl) return anyUrl;
                return doc.url || '';
            }
            // مصدر محدد (firebase أو cloudinary)
            const activeId = source === 'firebase' ? config.activeFirebase : config.activeCloudinary;
            if (activeId && doc.sources[activeId]) {
                return doc.sources[activeId];
            }
            // الصورة ليس لها رابط في المصدر المحدد — لا تعرض
            return '';
        }

        // صور قديمة بدون sources — تحقق من نوع الرابط
        if (source === 'auto') return doc.url || '';
        if (source === 'firebase' && doc.url && doc.url.includes('firebasestorage.googleapis.com')) return doc.url;
        if (source === 'cloudinary' && doc.url && doc.url.includes('res.cloudinary.com')) return doc.url;
        return '';
    };

    // الحصول على قائمة روابط بديلة للعرض عند فشل الرابط الأساسي
    // في الوضع المحدد: حسابات أخرى من نفس النوع فقط
    // في الوضع التلقائي: جميع المصادر
    const getFallbackUrls = (doc) => {
        if (!doc || !doc.sources) return [];

        const source = config.activeSource;
        const activeId = source === 'firebase' ? config.activeFirebase : config.activeCloudinary;
        const urls = [];

        // حسابات أخرى من نفس نوع المصدر
        const sameTypeAccounts = source === 'firebase'
            ? config.firebaseAccounts
            : config.cloudinaryAccounts;
        sameTypeAccounts.filter(a => a.enabled && a.id !== activeId).forEach(a => {
            if (doc.sources[a.id]) urls.push(doc.sources[a.id]);
        });

        // حسابات من المصدر الآخر + الرابط الأصلي — فقط في الوضع التلقائي
        if (source === 'auto') {
            const otherTypeAccounts = config.firebaseAccounts.concat(config.cloudinaryAccounts)
                .filter(a => a.enabled && a.id !== activeId && !sameTypeAccounts.find(s => s.id === a.id));
            otherTypeAccounts.forEach(a => {
                if (doc.sources[a.id] && !urls.includes(doc.sources[a.id])) urls.push(doc.sources[a.id]);
            });
            if (doc.url && !urls.includes(doc.url)) urls.push(doc.url);
        }

        return urls;
    };

    // الحصول على جميع الحسابات المفعلة (للرفع)
    const getEnabledAccounts = () => {
        return {
            cloudinary: config.cloudinaryAccounts.filter(a => a.enabled),
            firebase: config.firebaseAccounts.filter(a => a.enabled),
        };
    };

    // الحصول على الحساب النشط للرفع الأساسي
    const getActiveAccount = () => {
        if (config.activeSource === 'firebase') {
            return {
                type: 'firebase',
                account: config.firebaseAccounts.find(a => a.id === config.activeFirebase) || null,
            };
        }
        return {
            type: 'cloudinary',
            account: config.cloudinaryAccounts.find(a => a.id === config.activeCloudinary) || null,
        };
    };

    return {
        config,
        loading,
        getDisplayUrl,
        getFallbackUrls,
        getEnabledAccounts,
        getActiveAccount,
    };
};

export default useImageSource;
